/**
 * L∞seEnd™ analytics contract.
 *
 * Privacy: only the event names and property keys declared here may ever leave
 * the device. No user-generated content (message text, Loop titles/summaries,
 * search queries, names, emails, counterparties, evidence, filenames, calendar
 * titles, notification text, share tokens, exact dates or locations) is allowed.
 */

export const APPROVED_EVENTS = [
  "app_opened",
  "onboarding_started",
  "onboarding_completed",
  "conversation_started",
  "loop_capture_started",
  "loop_preview_generated",
  "loop_preview_edited",
  "loop_saved",
  "loop_inspector_opened",
  "resolution_queue_opened",
  "queue_action_started",
  "notification_center_opened",
  "notification_opened",
  "calendar_opened",
  "calendar_event_created",
  "search_opened",
  "evidence_added",
  "resolution_verification_started",
  "loop_closed",
  "loop_reopened",
  "share_dialog_opened",
  "share_link_created",
  "attachment_menu_opened",
  "attachment_added",
  "camera_capture_started",
  "camera_capture_completed",
] as const;

export type AnalyticsEvent = (typeof APPROVED_EVENTS)[number];

export const APPROVED_PROPERTIES = [
  "interface_surface",
  "loop_status",
  "action_type",
  "event_type",
  "notification_type",
  "attachment_type",
  "calendar_view",
  "device_category",
  "environment",
  "app_version",
  "success",
  "error_category",
  "duration_bucket",
] as const;

/**
 * Internal control properties attached centrally by the analytics utility.
 * They are approved for transmission but are NOT application dimensions.
 */
export const INTERNAL_CONTROL_PROPERTIES = ["$geoip_disable", "$process_person_profile"] as const;

export type AnalyticsPropertyKey = (typeof APPROVED_PROPERTIES)[number];

/** Only categorical / boolean values — never free-form user input. */
export type AnalyticsProperties = Partial<Record<AnalyticsPropertyKey, string | number | boolean>>;

export const APP_VERSION = "app-alpha";

/** Values allowed for the categorical properties we control. */
const MAX_VALUE_LENGTH = 48;
const VALUE_PATTERN = /^[a-z0-9_.:-]+$/;

/** Strips anything that isn't an approved, safely-shaped categorical value. */
export function sanitizeProperties(props?: AnalyticsProperties): AnalyticsProperties {
  if (!props) return {};
  const out: AnalyticsProperties = {};
  for (const key of APPROVED_PROPERTIES) {
    const value = props[key];
    if (value === undefined || value === null) continue;
    if (typeof value === "boolean" || typeof value === "number") {
      out[key] = value;
      continue;
    }
    const normalized = String(value).trim().toLowerCase().replace(/\s+/g, "_");
    if (!normalized || normalized.length > MAX_VALUE_LENGTH) continue;
    if (!VALUE_PATTERN.test(normalized)) continue;
    out[key] = normalized;
  }
  return out;
}

/** Coarse buckets so we never transmit precise timings or dates. */
export function durationBucket(ms: number): string {
  if (ms < 1_000) return "under_1s";
  if (ms < 5_000) return "1s_5s";
  if (ms < 30_000) return "5s_30s";
  if (ms < 300_000) return "30s_5m";
  return "over_5m";
}

/* ------------------------------------------------------- route normalization */

/** Route templates — the only route shapes allowed to leave the device. */
const ROUTE_TEMPLATES: Array<[RegExp, string]> = [
  [/^\/app\/chat\/[^/]+/, "/app/chat/:conversationId"],
  [/^\/app\/loops\/[^/]+/, "/app/loops/:loopId"],
  [/^\/share\/[^/]+/, "/share/:token"],
  [/^\/app\/calendar/, "/app/calendar"],
  [/^\/app\/queue/, "/app/queue"],
  [/^\/app\/notifications/, "/app/notifications"],
  [/^\/app\/search/, "/app/search"],
  [/^\/app\/chaos-map/, "/app/chaos-map"],
  [/^\/app\/settings/, "/app/settings"],
  [/^\/app\/?$/, "/app"],
  [/^\/$/, "/"],
];

/** Turns any path (or full URL) into a static template with no identifiers. */
export function normalizeRoute(input?: string | null): string {
  if (!input) return "unknown";
  let path = String(input);
  // Drop origin, query string and fragment before matching.
  path = path.replace(/^[a-z]+:\/\/[^/]+/i, "");
  path = path.split("?")[0]!.split("#")[0]!;
  for (const [pattern, template] of ROUTE_TEMPLATES) {
    if (pattern.test(path)) return template;
  }
  return "other";
}

/* ------------------------------------------------------------ QA guardrails */

const UNSAFE_VALUE_PATTERNS: RegExp[] = [
  /^conv_/i,
  /^loop_/i,
  /^invite_/i,
  /\/share\//i,
  /token=/i,
  /[?#]/,
  /[\w.+-]+@[\w-]+\.[a-z]{2,}/i,
];

/** True when a value looks like an identifier, URL, query string or email. */
export function isUnsafeValue(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  if (UNSAFE_VALUE_PATTERNS.some((re) => re.test(v))) return true;
  // Free-form text: whitespace or characters outside our categorical shape.
  if (
    v !== "/" &&
    !ROUTE_TEMPLATES.some(([, t]) => t === v) &&
    !VALUE_PATTERN.test(v.toLowerCase())
  )
    return true;
  return false;
}

/** Dev-only rejection check: returns the offending key, or null when clean. */
export function findUnsafeProperty(props: Record<string, unknown>): string | null {
  for (const [key, value] of Object.entries(props)) {
    if (isUnsafeValue(value)) return key;
  }
  return null;
}

/* --------------------------------------------- PostHog default-prop scrubber */

const DROP_KEYS = new Set([
  "$referrer",
  "$referring_domain",
  "$initial_referrer",
  "$initial_referring_domain",
  "$initial_current_url",
  "$initial_pathname",
  "$host",
  "$initial_host",
  "$set",
  "$set_once",
  // GeoIP / location enrichment — never transmitted or stored.
  "$geoip_latitude",
  "$geoip_longitude",
  "$geoip_city_name",
  "$geoip_city_confidence",
  "$geoip_postal_code",
  "$geoip_subdivision_1_name",
  "$geoip_subdivision_1_code",
  "$geoip_subdivision_2_name",
  "$geoip_subdivision_2_code",
  "$geoip_country_name",
  "$geoip_country_code",
  "$geoip_continent_name",
  "$geoip_continent_code",
  "$geoip_time_zone",
  "$ip",
]);

/** True for any location property other than the `$geoip_disable: true` control. */
export function isDisallowedGeoProperty(key: string, value: unknown): boolean {
  if (key === "$geoip_disable") return value !== true;
  return key.startsWith("$geoip_") || key.startsWith("$initial_geoip_");
}

/**
 * Removes/redacts every URL-bearing PostHog default property so no exact
 * route, token or identifier is ever transmitted. Every `$geoip_*` /
 * `$initial_geoip_*` property is stripped except `$geoip_disable: true`.
 */
export function scrubDefaultProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (DROP_KEYS.has(key)) continue;
    if (isDisallowedGeoProperty(key, value)) continue;
    if (key === "$current_url" || key === "$pathname" || key === "$initial_pathname") continue;
    if (typeof value === "string" && (value.includes("://") || isUnsafeValue(value))) continue;
    out[key] = value;
  }

  return out;
}
